<template>
  <ez-wizard fullWidth @exit="onWizardExit">
    <template #title>Missing Page</template>
    <template #actions>
      <ez-button class="p-0" type="link" @click="onRemovePendingUpdate">
        <font-awesome-icon icon="trash" />
      </ez-button>
    </template>
    <div class="merge-view">
      <ez-loader :show="isLoading">Loading...</ez-loader>
      <div v-if="selectedFile.image" class="selected-file">
        <div class="selected-file__container">
          <VPdfViewer
            v-if="selectedFile && selectedFile.type === 'pdf'"
            :src="selectedFile.image"
            :show-all-pages="false"
            :controls="true"
            :name="selectedFile.name"
            :id="selectedFile.id"
          />
          <v-image-viewer v-else :file="selectedFile" :controls="true" />
        </div>
        <div class="actions">
          <ez-button
            :type="fileChecked(selectedFile.id) ? 'primary' : 'secondary'"
            @click="selectFile(selectedFile)"
          >
            <font-awesome-icon v-if="fileChecked(selectedFile.id)" icon="check" />
            {{ fileChecked(selectedFile.id) ? 'Selected' : 'Select' }}
          </ez-button>
          <ez-input
            class="ml-16"
            label="Page Number"
            type="number"
            :value="getPageNumber(selectedFile) || null"
            formKey="merge-invoice"
            name="pageNumber"
            :minValue="1"
            @onChange="updatePageNumber(selectedFile, $event)"
          />
        </div>
      </div>
      <div>
        <div class="processing-actions">
          <ez-button
            type="secondary"
            :class="{ selected: note === 'missing_page' }"
            @click="selectNote('missing_page')"
          >
            Missing Page
          </ez-button>
          <ez-button
            type="secondary"
            :class="{ selected: note === 'blurry_image' }"
            @click="selectNote('blurry_image')"
          >
            Blur Invoice
          </ez-button>
          <ez-button
            type="secondary"
            :class="{ selected: note === 'not_valid' }"
            @click="selectNote('not_valid')"
          >
            Not Valid
          </ez-button>
          <ez-button
            type="secondary"
            :class="{ selected: note === 'missing_info' }"
            @click="selectNote('missing_info')"
          >
            Info Missing
          </ez-button>
          <ez-button :disabled="isCompleting" @click="openUploadInvoice"> Upload File </ez-button>
          <ez-button @click="mergeInvoices" :disabled="isCompleting">Merge</ez-button>
        </div>
        <div class="files-wrapper">
          <div
            :class="['file', { 'file--selected': fileChecked(file.id) }]"
            v-for="file in invoiceFiles"
            :key="file.id"
          >
            <img
              class="checkmark"
              v-if="fileChecked(file.id)"
              src="@/assets/lite-badge.svg"
              @click.stop="selectFile(file)"
            />
            <div class="file-details" @click="selectFile(file)">
              <p>{{ file.pendingInvoice.distributor?.name || 'Missing distributor name' }}</p>
              <p>
                Invoice No. <span>{{ file.pendingInvoice.invoiceNumber || '-' }}</span>
              </p>
              <p>
                Invoice Date <span>{{ formatDate(file.pendingInvoice.invoiceDate) || '-' }}</span>
              </p>
            </div>
            <div class="file-content">
              <VPdfViewer
                v-if="file.type === 'pdf'"
                :src="file.image"
                :show-all-pages="false"
                @click="selectFile(file)"
              />
              <img v-else :src="file.image" @click="selectFile(file)" />
              <ez-input
                class="mt-12"
                label="Page Number"
                type="number"
                :value="getPageNumber(file) || null"
                formKey="merge-invoice"
                name="pageNumber"
                :minValue="1"
                @onChange="updatePageNumber(file, $event)"
              />
            </div>
          </div>
        </div>
        <div v-if="isLoadingMore" class="u-text-center mt-12">
          <ez-spinner />
        </div>
        <ez-load-more @loadMore="onLoadMore" v-if="meta.page < meta.lastPage && !isLoadingMore" />
        <ez-confirmation-modal ref="deleteModal" type="red">
          <template #title>Remove Invoice?</template>
          <template #content>
            <p>This invoice will be deleted from the list.</p>
          </template>
          <template #footer>
            <ez-button @click="closeDeleteModal" type="link">Cancel</ez-button>
            <ez-button @click="deleteInvoice" type="red">Remove Invoice</ez-button>
          </template>
        </ez-confirmation-modal>
        <upload-invoice
          ref="uploadInvoice"
          @success="invoicesUploaded"
          :isReplaceOrAdd="true"
          :action="`/venues/${venueId}/pending-invoices/${invoiceId}/invoices?replace=false`"
        />
      </div>
    </div>
  </ez-wizard>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import { formatDate } from '@/util/utils';
import { LOADING_KEY } from '@/util/constants';
import flash from '@/components/ui/FlashMessage';
import EzWizard from '@/components/layout/Wizard.vue';
import VPdfViewer from '@/components/v3/elements/VPdfViewer';
import VImageViewer from '@/components/v3/elements/VImageViewer';
import EzButton from '@/components/ui/Button/EzButton.vue';
import EzLoadMore from '@/components/ui/LoadMore';
import EzInput from '@/components/ui/Input/EzInput.vue';
import EzLoader from '@/components/ui/Loader/EzLoader';
import EzSpinner from '@/components/ui/Spinner/EzSpinner.vue';
import { EzConfirmationModal } from '@/components/ui/Modal';
import UploadInvoice from '@/views/platform/venue/orders/actions/UploadInvoice.vue';

export default {
  components: {
    EzWizard,
    VPdfViewer,
    VImageViewer,
    EzButton,
    EzLoadMore,
    EzInput,
    EzLoader,
    EzSpinner,
    EzConfirmationModal,
    UploadInvoice,
  },
  props: {
    invoiceId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      invoiceFiles: [],
      meta: {},
      formatDate,
      selectedFiles: [],
      filesWithPages: [],
      selectedFile: { image: null },
      note: null,
      venue: {},
    };
  },
  computed: {
    ...mapGetters('loading', ['getLoading']),
    ...mapState('entities/users', ['contextId']),
    venueId() {
      return this.venue?.id ?? this.contextId;
    },
    isLoading() {
      return this.getLoading(LOADING_KEY.FETCH_ADMIN_PENDING_INVOICE_MERGE);
    },
    isLoadingMore() {
      return this.getLoading(LOADING_KEY.FETCH_ADMIN_PENDING_INVOICE_MERGE_LOAD_MORE);
    },
    isCompleting() {
      return this.getLoading(LOADING_KEY.ADMIN_PENDING_INVOICE_MERGE);
    },
  },
  methods: {
    ...mapActions('entities/orders', ['venuePendingInvoiceMerge']),
    ...mapActions('entities/distributors', ['distributorGetMergePendingUpdate']),
    ...mapActions('entities/venues', ['outletGetPendingInvoices', 'outletDeletePendingInvoice']),
    onWizardExit() {
      this.$router.push({ name: 'venue-pending-updates' });
    },
    onRemovePendingUpdate() {
      this.openDeleteModal();
    },
    selectNote(note) {
      if (this.note === note) this.note = null;
      else this.note = note;
    },
    openDeleteModal() {
      this.$refs.deleteModal.open();
    },
    closeDeleteModal() {
      this.$refs.deleteModal.close();
    },
    async deleteInvoice() {
      try {
        await this.outletDeletePendingInvoice({
          venueId: this.venueId,
          pendingId: this.invoiceId,
        });
        this.closeDeleteModal();
        flash.success({
          title: 'Invoice successfully deleted!',
        });
        this.$router.push({ name: 'venue-pending-updates' });
      } catch (e) {
        flash.error({
          title: 'Something went wrong!',
        });
      }
    },
    async onLoadMore() {
      const { data } = await this.distributorGetMergePendingUpdate({
        venueId: this.venueId,
        invoiceId: this.invoiceId,
        page: this.meta.page + 1,
        loadingKey: LOADING_KEY.FETCH_ADMIN_PENDING_INVOICE_MERGE_LOAD_MORE,
      });
      this.invoiceFiles = [...this.invoiceFiles, ...data.data];
      this.meta = data.meta;
    },
    selectFile(file) {
      const allFiles = this.invoiceFiles.filter(
        invoice => invoice.pendingInvoice.id === file.pendingInvoice.id,
      );
      if (this.selectedFiles.includes(file.id)) {
        if (file.pendingInvoice.id === this.invoiceId) {
          this.selectedFiles = this.selectedFiles.filter(item => item !== file.id);
          [this.selectedFile] = this.invoiceFiles;
        } else {
          allFiles.forEach(f => {
            this.selectedFiles = this.selectedFiles.filter(item => item !== f.id);
          });
          [this.selectedFile] = this.invoiceFiles;
        }
      } else {
        if (file.pendingInvoice.id === this.invoiceId) {
          this.selectedFiles.push(file.id);
        } else {
          allFiles.forEach(f => {
            if (!this.selectedFiles.includes(f.id)) this.selectedFiles.push(f.id);
          });
        }
        this.selectedFile = file;
      }
    },
    fileChecked(id) {
      return this.selectedFiles.includes(id);
    },
    updatePageNumber(file, event) {
      const index = this.filesWithPages.findIndex(item => item.id === file.id);
      if (index === -1) {
        this.filesWithPages.push({
          id: file.id,
          pageNumber: event,
        });
        if (!this.fileChecked(file.id)) this.selectFile(file);
      } else {
        this.filesWithPages[index].pageNumber = event;
      }
    },
    getPageNumber(file) {
      const index = this.filesWithPages.findIndex(item => item.id === file.id);
      if (index === -1) {
        return null;
      }
      return this.filesWithPages[index].pageNumber;
    },
    async mergeInvoices() {
      const body = {
        invoices: [],
        ...(this.note ? { note: this.note } : {}),
      };
      this.selectedFiles.forEach(file => {
        body.invoices.push({
          pendingInvoiceId: this.invoiceFiles.filter(item => item.id === file)[0].pendingInvoice.id,
          id: file,
          page: +this.getPageNumber({ id: file }),
        });
      });
      try {
        await this.venuePendingInvoiceMerge({
          venueId: this.venueId,
          invoiceId: this.invoiceId,
          body,
        });
        flash.success({ title: 'Pending invoice files successfully merged' });
        this.$router.push({ name: 'venue-pending-updates' });
      } catch (e) {
        flash.error({
          title: 'Something went wrong!',
          message: e.response.data?.error?.message ?? '',
        });
      }
    },
    openUploadInvoice() {
      this.$refs.uploadInvoice.open();
    },
    async invoicesUploaded() {
      const { data } = await this.distributorGetMergePendingUpdate({
        venueId: this.venueId,
        invoiceId: this.invoiceId,
        page: 1,
      });
      this.invoiceFiles = data.data;
      this.meta = data.meta;
      [this.selectedFile] = this.invoiceFiles;
      const allFiles = this.invoiceFiles.filter(
        invoice => invoice.pendingInvoice.id === this.invoiceId,
      );
      this.venue = this.invoiceFiles.filter(
        invoice => invoice.pendingInvoice.id === this.invoiceId,
      )[0].pendingInvoice.venue;
      allFiles.forEach(f => {
        this.selectedFiles.push(f.id);
        this.filesWithPages.push({
          id: f.id,
          pageNumber: f.index + 1,
        });
      });
    },
  },
  async beforeMount() {
    const { data } = await this.distributorGetMergePendingUpdate({
      venueId: this.venueId,
      invoiceId: this.invoiceId,
      page: 1,
    });
    this.invoiceFiles = data.data;
    this.meta = data.meta;
    [this.selectedFile] = this.invoiceFiles;
    const allFiles = this.invoiceFiles.filter(
      invoice => invoice.pendingInvoice.id === this.invoiceId,
    );
    this.venue = this.invoiceFiles.filter(
      invoice => invoice.pendingInvoice.id === this.invoiceId,
    )[0].pendingInvoice.venue;
    allFiles.forEach(f => {
      this.selectedFiles.push(f.id);
      this.filesWithPages.push({
        id: f.id,
        pageNumber: f.index + 1,
      });
    });
  },
};
</script>

<style lang="scss" scoped>
:deep() .ez-wizard__main {
  max-width: none;
  padding: 32px 60px;

  .ez-wizard__page-header {
    margin: 0;
  }
}

.merge-view {
  display: flex;
  justify-content: center;
  align-items: flex-start;
}

.selected-file {
  position: sticky;
  top: 80px;
  flex: 1 1 100%;
  max-width: 420px;
  margin-right: 48px;

  &__container {
    width: 100%;
    display: inline-block;
    height: 560px;
    overflow-x: auto;
    overflow-y: auto;
    border: 1px solid #e1e5ed;
    border-radius: 3px;
    background-color: #e9ebf2;
  }
  :deep() .pdf-container {
    border: none;
    border-radius: 0;
    background-color: transparent;
  }

  .actions {
    margin-top: 40px;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }
}

.processing-actions {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 32px;
  flex-wrap: wrap;
  row-gap: 12px;

  :deep() .button {
    min-width: 146px;
    & + .button {
      margin-left: 32px;
    }
    display: flex;
    align-items: center;
    justify-content: center;

    &.button--secondary {
      border: 2px solid $color-gray-F5;
      &.selected {
        border: 2px solid $color-primary-blue;
      }
    }
  }
}

.files-wrapper {
  max-width: 860px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  grid-column-gap: 16px;
  grid-row-gap: 16px;

  .file {
    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 200px;
    border-radius: 5px;
    border: 2px solid #eceef5;
    p {
      margin: 0;
      font-size: 14px;
      line-height: 1.5;
      color: $color-gray-25;
      & + p {
        margin-top: 4px;
      }
      span {
        color: $color-gray-6C;
      }
    }
    &--selected {
      border: 2px solid $color-primary-blue;
    }
    &-details {
      padding: 16px;
      padding-right: 24px;
      cursor: pointer;
    }
    &-content {
      display: flex;
      flex-direction: column;
      flex: 1;
      justify-content: space-between;
      align-items: center;
      padding: 30px 30px 20px 30px;
      background-color: $color-gray-F5;

      :deep() .button {
        margin-top: 20px;
      }
    }
    .checkmark {
      position: absolute;
      top: 8px;
      right: 8px;
      width: 18px;
      height: 18px;
    }

    :deep() .pdf-page-canvas,
    img {
      width: 100%;
      cursor: pointer;
    }
  }
}

.dropdown-button {
  :deep() .button.button--primary {
    padding-right: 6px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    min-width: initial;
    display: inline-block;
    margin-left: 32px;
  }
  :deep() .button-dropdown.button-dropdown--primary {
    padding-left: 0px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;

    .button.button--primary {
      padding-left: 6px;
      padding-right: 12px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
      border-top-right-radius: 3px;
      border-bottom-right-radius: 3px;
      margin-left: 0;
    }
    .button.button--secondary {
      min-width: initial;
      margin-left: 0;
      justify-content: flex-start;
      border: none;
      background-color: #fff;

      &:hover {
        background-color: #f5f6fa;
      }
    }
  }
}
</style>
